
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { installErrorMonitoring } from '@/lib/monitoring'
import ErrorBoundary from '@/components/ErrorBoundary'
import { initPwa, pwaLog } from '@/lib/pwa'


// Global browser error + unhandled rejection capture, installed before render
// so a crash during the first paint is still reported.
installErrorMonitoring();

// PWA install state. Must run BEFORE React renders because Chromium can fire
// `beforeinstallprompt` immediately after the document loads — if no listener
// exists at that moment the event is lost for the whole page view.
initPwa();

// Service worker: offline shell + hashed-asset cache (see public/sw.js).
// Registered after load so it never competes with the first paint.
//
// Chrome will NOT offer an install until this worker is registered, activated
// and has a fetch handler, so the outcome is logged (success AND failure) —
// a silent catch here was hiding the most common install blocker.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        pwaLog('service worker registered', {
          scope: reg.scope,
          state: reg.active?.state || reg.installing?.state || reg.waiting?.state || 'unknown',
        });
        reg.addEventListener('updatefound', () => {
          const sw = reg.installing;
          if (!sw) return;
          sw.addEventListener('statechange', () => pwaLog('service worker state', { state: sw.state }));
        });
      })
      .catch((err) => {
        // Most common causes: /sw.js served with the wrong MIME type by an SPA
        // fallback, or a scope violation. Both silently block installability.
        pwaLog('service worker registration FAILED', { message: (err as Error)?.message });
      });
  });
} else {
  pwaLog('service worker unsupported in this context');
}


createRoot(document.getElementById("root")!).render(
  <ErrorBoundary area="root">
    <App />
  </ErrorBoundary>
);
